import React, { useState, useEffect } from 'react';
import {
    Card,
    Input,
    Select,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Button,
    Badge,
    Tooltip,
    Dropdown,
    MenuProps,
    message,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import {
    CustomerServiceOutlined,
    SearchOutlined,
    ReloadOutlined,
    UserOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    MoreOutlined,
} from '@ant-design/icons';
import { useGetAdminTicketsQuery, useGetAdminTicketStatsQuery, useGetTicketCategoriesQuery, useUpdateTicketMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { TicketDetailDrawer } from '../components/TicketDetailDrawer';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ITicket, ITicketCategory } from '../../services/nestjs-base-api/types';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Status badge component
const TicketStatusBadge: React.FC<{ status: ITicket['status'] }> = ({ status }) => {
    const statusConfig = {
        open: { color: 'blue', icon: <ExclamationCircleOutlined />, text: 'Open' },
        in_progress: { color: 'processing', icon: <ClockCircleOutlined />, text: 'In Progress' },
        waiting_on_customer: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Waiting on Customer' },
        resolved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Resolved' },
        closed: { color: 'default', icon: <CheckCircleOutlined />, text: 'Closed' },
    };

    const config = statusConfig[status];
    return (
        <Badge
            status={config.color as any}
            text={
                <Space size={4}>
                    {config.icon}
                    {config.text}
                </Space>
            }
        />
    );
};

// Priority badge component
const TicketPriorityBadge: React.FC<{ priority: ITicket['priority'] }> = ({ priority }) => {
    const priorityConfig = {
        low: { color: 'default', text: 'Low' },
        normal: { color: 'blue', text: 'Normal' },
        high: { color: 'orange', text: 'High' },
        urgent: { color: 'red', text: 'Urgent' },
    };

    const config = priorityConfig[priority];
    return <Tag color={config.color}>{config.text}</Tag>;
};

const AdminSupportPage: React.FC = () => {
    const { user } = useAuth();
    const { ticketHash } = useParams<{ ticketHash?: string }>();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [priorityFilter, setPriorityFilter] = useState<string | undefined>();
    const [categoryFilter, setCategoryFilter] = useState<number | undefined>();
    const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'mine' | 'unassigned'>('all');
    const [selectedTicketHash, setSelectedTicketHash] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);

    // Build query params based on assignment filter
    const getAssignmentParam = () => {
        if (assignmentFilter === 'mine') return user?.id;
        if (assignmentFilter === 'unassigned') return null;
        return undefined; // 'all' - don't filter by assignment
    };

    // Fetch data
    const {
        data: ticketsData,
        isLoading,
        refetch,
    } = useGetAdminTicketsQuery({
        jwtToken: user?.jwtToken || '',
        params: {
            search: search || undefined,
            status: statusFilter,
            priority: priorityFilter,
            categoryId: categoryFilter,
            assignedToUserId: getAssignmentParam(),
            sortBy: 'created',
            sortOrder: 'DESC',
        },
    });

    const { data: statsData } = useGetAdminTicketStatsQuery({ jwtToken: user?.jwtToken || '' });
    const { data: categories } = useGetTicketCategoriesQuery({ jwtToken: user?.jwtToken || '' });

    // Mutation for assigning tickets
    const [updateTicket] = useUpdateTicketMutation();

    const tickets = ticketsData?.tickets || [];
    const total = ticketsData?.total || 0;

    // Handle assign to me
    const handleAssignToMe = async (ticketHash: string) => {
        try {
            await updateTicket({
                jwtToken: user?.jwtToken || '',
                hash: ticketHash,
                data: { assignedToUserId: user?.id },
            }).unwrap();
            message.success('Ticket assigned to you');
            refetch();
        } catch (error) {
            message.error('Failed to assign ticket');
        }
    };

    // Handle URL parameter for direct ticket access
    useEffect(() => {
        if (ticketHash && ticketHash !== selectedTicketHash) {
            setSelectedTicketHash(ticketHash);
            setDrawerVisible(true);
        }
    }, [ticketHash, selectedTicketHash]);

    // Table columns
    const columns: ColumnsType<ITicket> = [
        {
            title: 'Ticket ID',
            dataIndex: 'hash',
            key: 'hash',
            width: 150,
            render: (hash: string) => (
                <Text code copyable={{ text: hash }} style={{ whiteSpace: 'nowrap' }}>
                    {hash.substring(0, 8)}...
                </Text>
            ),
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
            render: (subject: string, record: ITicket) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{subject}</div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.description.substring(0, 60)}
                        {record.description.length > 60 ? '...' : ''}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 150,
            render: (category: ITicketCategory) => (
                category ? <Tag color={category.color || 'default'}>{category.name}</Tag> : <Text type="secondary">None</Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 180,
            filters: [
                { text: 'Open', value: 'open' },
                { text: 'In Progress', value: 'in_progress' },
                { text: 'Waiting on Customer', value: 'waiting_on_customer' },
                { text: 'Resolved', value: 'resolved' },
                { text: 'Closed', value: 'closed' },
            ],
            render: (status: ITicket['status']) => <TicketStatusBadge status={status} />,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            filters: [
                { text: 'Low', value: 'low' },
                { text: 'Normal', value: 'normal' },
                { text: 'High', value: 'high' },
                { text: 'Urgent', value: 'urgent' },
            ],
            render: (priority: ITicket['priority']) => <TicketPriorityBadge priority={priority} />,
        },
        {
            title: 'Created By',
            dataIndex: 'createdByUser',
            key: 'createdByUser',
            width: 150,
            render: (user: any) => (
                <Space>
                    <UserOutlined />
                    <span>
                        {user.firstName} {user.lastName}
                    </span>
                </Space>
            ),
        },
        {
            title: 'Organisation',
            dataIndex: 'organisation',
            key: 'organisation',
            width: 150,
            render: (organisation: any) => (
                organisation ? (
                    <Tag color="purple">{organisation.name}</Tag>
                ) : (
                    <Text type="secondary">N/A</Text>
                )
            ),
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedToUser',
            key: 'assignedToUser',
            width: 150,
            render: (user: any) =>
                user ? (
                    <Space>
                        <UserOutlined />
                        <span>
                            {user.firstName} {user.lastName}
                        </span>
                    </Space>
                ) : (
                    <Text type="secondary">Unassigned</Text>
                ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => (
                <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
                    <span>{dayjs(date).fromNow()}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right' as const,
            render: (_: any, record: ITicket) => {
                const items: MenuProps['items'] = [
                    {
                        key: 'view',
                        label: 'View Details',
                        onClick: () => {
                            navigate(`/admin/support/${record.hash}`);
                        },
                    },
                    {
                        key: 'assign',
                        label: record.assignedToUserId === user?.id ? 'Already assigned to you' : 'Assign to Me',
                        disabled: record.assignedToUserId === user?.id,
                        onClick: () => handleAssignToMe(record.hash),
                    },
                ];

                return (
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                );
            },
        },
    ];

    // Stats cards
    const renderStatsCards = () => {
        if (!statsData) return null;

        const statusCounts = statsData.byStatus.reduce(
            (acc, item) => {
                acc[item.status] = item.count;
                return acc;
            },
            {} as Record<string, number>,
        );

        return (
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">Open Tickets</Text>
                            <Title level={2} style={{ margin: '8px 0' }}>
                                {statusCounts.open || 0}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">In Progress</Text>
                            <Title level={2} style={{ margin: '8px 0' }}>
                                {statusCounts.in_progress || 0}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">Avg Response Time</Text>
                            <Title level={2} style={{ margin: '8px 0' }}>
                                {statsData.avgResponseTimeSeconds
                                    ? `${Math.round(statsData.avgResponseTimeSeconds / 3600)}h`
                                    : 'N/A'}
                            </Title>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary">Total Tickets</Text>
                            <Title level={2} style={{ margin: '8px 0' }}>
                                {total}
                            </Title>
                        </div>
                    </Card>
                </Col>
            </Row>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Title level={2}>
                    <CustomerServiceOutlined style={{ marginRight: 8 }} />
                    Support Tickets
                </Title>
                <Text type="secondary">
                    Manage and respond to customer support tickets
                </Text>
            </div>

            {/* Stats Cards */}
            {renderStatsCards()}

            {/* Assignment Filter Buttons */}
            <Card style={{ marginBottom: 16 }}>
                <Space size="middle">
                    <Button
                        type={assignmentFilter === 'all' ? 'primary' : 'default'}
                        onClick={() => setAssignmentFilter('all')}
                    >
                        All Tickets
                    </Button>
                    <Button
                        type={assignmentFilter === 'mine' ? 'primary' : 'default'}
                        icon={<UserOutlined />}
                        onClick={() => setAssignmentFilter('mine')}
                    >
                        My Tickets
                    </Button>
                    <Button
                        type={assignmentFilter === 'unassigned' ? 'primary' : 'default'}
                        onClick={() => setAssignmentFilter('unassigned')}
                    >
                        Unassigned
                    </Button>
                </Space>
            </Card>

            {/* Filters */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Search
                            placeholder="Search tickets..."
                            allowClear
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            placeholder="Status"
                            allowClear
                            style={{ width: '100%' }}
                            value={statusFilter}
                            onChange={setStatusFilter}
                        >
                            <Option value="open">Open</Option>
                            <Option value="in_progress">In Progress</Option>
                            <Option value="waiting_on_customer">Waiting on Customer</Option>
                            <Option value="resolved">Resolved</Option>
                            <Option value="closed">Closed</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            placeholder="Priority"
                            allowClear
                            style={{ width: '100%' }}
                            value={priorityFilter}
                            onChange={setPriorityFilter}
                        >
                            <Option value="low">Low</Option>
                            <Option value="normal">Normal</Option>
                            <Option value="high">High</Option>
                            <Option value="urgent">Urgent</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            placeholder="Category"
                            allowClear
                            style={{ width: '100%' }}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                        >
                            {categories?.map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Tickets Table */}
            <Card>
                <ResponsiveTable
                    columns={columns}
                    dataSource={tickets}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        total,
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} tickets`,
                    }}
                    onRow={(record) => {
                        const isResolved = record.status === 'resolved' || record.status === 'closed';
                        return {
                            onClick: () => {
                                navigate(`/admin/support/${record.hash}`);
                            },
                            style: {
                                cursor: 'pointer',
                                backgroundColor: isResolved ? '#f6ffed' : undefined,
                                opacity: isResolved ? 0.75 : 1,
                            },
                        };
                    }}
                />
            </Card>

            {/* Ticket Detail Drawer */}
            <TicketDetailDrawer
                ticketHash={selectedTicketHash}
                visible={drawerVisible}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedTicketHash(null);
                    navigate('/admin/support');
                    refetch(); // Refresh the list when drawer closes
                }}
            />
        </div>
    );
};

export default AdminSupportPage;
